import { IPoint2d } from '@/editor/math/IPoint2d';
import { IFullLayoutBase, IFullLayoutResultData, ILayoutParams } from '@/service/layout/types';
import { IScheme } from '@/service/scheme/types';
import { printImage } from './log';
import { EColumnType, IColumnData } from '@/editor/data/model/model';

export function parseLayoutResult(data: unknown) {
  // @ts-expect-error 暴露原始数据到全局，方便调试
  window.layoutResultData = data;
  const { response, payload } = data as {
    response: IFullLayoutResultData;
    payload: ILayoutParams;
  };

  // @ts-expect-error 调试数据
  const scheme: IScheme = {
    schemeJson: {
      wallDatas: payload.baseMessage.wallDatas,
      doorWindowDatas: payload.baseMessage.doorWindowDatas,
      columnDatas: payload.baseMessage.columnDatas,
      version: 1,
      furnitureDatas: [],
      faceDatas: {
        sepWallFaceDatas: [],
        holeFaceDatas: [],
        wallFaceDatas: [],
        columnFaceDatas: []
      },
      regionDatas: [],
      roomDatas: [],
      separateWallDatas: {
        baseSeparateWallDatas: [],
        separateWallDatas: []
      },
      systemParams: payload.baseMessage.systemParams
    }
  };

  return {
    scheme,
    layoutResults: response
  };
}

function randomColor() {
  let index = 0;
  const colorList = ['#bf616a', '#d08770', '#ebcb8b', '#a3be8c', '#b48ead'];
  return () => {
    index = (index + 1) % colorList.length;
    return colorList[index];
  };
}

interface IDrawSegParams {
  path: { start: IPoint2d; end: IPoint2d }[];
  hole: { start: IPoint2d; end: IPoint2d }[][];
  column: IColumnData[];
  doorWindows: IFullLayoutBase['doorWindowDatas'];
  direction?: boolean;
  title?: string;
}

function drawPreview({ path, hole, column, doorWindows, direction = true, title }: IDrawSegParams) {
  const canvas = document.createElement('canvas');

  const maxW = Math.max(...path.map((seg) => Math.max(seg.start.x, seg.end.x)));
  const minW = Math.min(...path.map((seg) => Math.min(seg.start.x, seg.end.x)));
  const maxH = Math.max(...path.map((seg) => Math.max(seg.start.y, seg.end.y)));
  const minH = Math.min(...path.map((seg) => Math.min(seg.start.y, seg.end.y)));

  const width = maxW - minW;
  const height = maxH - minH;

  const scale = Math.min(984 / width, 984 / height);

  canvas.width = width * scale + 20;
  canvas.height = height * scale + (title ? 50 : 20);

  const getNextColor = randomColor();
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return;
  }

  // 背景颜色
  ctx.fillStyle = '#eceff4';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // 上下翻转
  ctx.translate(0, canvas.height);
  ctx.scale(1, -1);
  ctx.lineWidth = 4;

  // 绘制墙

  path.forEach((seg) => {
    const color = getNextColor();
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.moveTo((seg.start.x - minW) * scale + 10, (seg.start.y - minH) * scale + 10);
    ctx.lineTo((seg.end.x - minW) * scale + 10, (seg.end.y - minH) * scale + 10);
    ctx.stroke();
    if (direction) {
      // 画三角形表示线条方向
      const angle = Math.atan2(seg.end.y - seg.start.y, seg.end.x - seg.start.x);
      ctx.save();
      ctx.translate((seg.end.x - minW) * scale + 10, (seg.end.y - minH) * scale + 10);
      ctx.rotate(angle);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(-8, -8);
      ctx.lineTo(-8, 8);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
      ctx.restore();
    }
  });

  // 绘制洞
  hole.forEach((holes) => {
    holes.forEach((seg) => {
      const color = getNextColor();
      ctx.strokeStyle = `${color}99`;
      ctx.beginPath();
      ctx.moveTo((seg.start.x - minW) * scale + 10, (seg.start.y - minH) * scale + 10);
      ctx.lineTo((seg.end.x - minW) * scale + 10, (seg.end.y - minH) * scale + 10);
      ctx.stroke();
      if (direction) {
        // 画三角形表示线条方向
        const angle = Math.atan2(seg.end.y - seg.start.y, seg.end.x - seg.start.x);
        ctx.save();
        ctx.translate((seg.end.x - minW) * scale + 10, (seg.end.y - minH) * scale + 10);
        ctx.rotate(angle);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(-8, -8);
        ctx.lineTo(-8, 8);
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();
        ctx.restore();
      }
    });
  });

  // 绘制柱子
  column.forEach((col) => {
    ctx.fillStyle = '#2e3440';
    const x = (col.center.x - minW) * scale + 10;
    const y = (col.center.y - minH) * scale + 10;
    switch (col.type) {
      case EColumnType.cylinder: {
        const r = col.radius * scale;
        ctx.beginPath();
        ctx.ellipse(x, y, r, r, 0, 0, 2 * Math.PI);
        ctx.fill();
        break;
      }
      case EColumnType.squareColumn: {
        const w = col.width * scale;
        const h = col.thickness * scale;
        ctx.fillRect(x - w / 2, y - h / 2, w, h);
        break;
      }
    }
  });

  // 绘制门窗
  doorWindows.forEach((dw) => {
    ctx.fillStyle = dw.entrance ? '#2e3440' : '#d8dee9';
    const rotation = dw.rotation;
    const x = (dw.center.x - minW) * scale + 10;
    const y = (dw.center.y - minH) * scale + 10;
    const w = dw.width * scale;
    const h = 12;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.fillRect(-w / 2, -h / 2, w, h);
    ctx.restore();
  });

  if (title) {
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#2e3440';
    ctx.font = '16px Arial';
    const textWidth = ctx.measureText(title).width;
    ctx.fillText(title, (canvas.width - textWidth) / 2, 30);
    ctx.restore();
  }
  printImage(canvas.toDataURL());
}

export { drawPreview };
